import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Shield, Search, ExternalLink } from 'lucide-react';
import { heroCarousel } from '../lib/supabase';

// Icon mapping
const ICONS = {
  Shield,
  Search,
  ExternalLink
};

// Fallback copy, used only when no slide is published. The keys were
// knowYourRights / stoppedByPolice / lookUpOffence — identifiers inherited
// from the road-traffic app this project was migrated from, long after the
// values had been rewritten for schools.
const T = {
  en: {
    headline: "Jamaica Schools Guide",
    headlineAccent: "Your Complete Education Resource",
    description: "PEP, CSEC & CAPE exam guides, school rankings, scholarships, UWI & UTech admissions, HEART Trust training & more — everything Jamaican students and parents need in one place.",
    primaryCta: "Find Schools",
    secondaryCta: "Exam Guides"
  },
  patois: {
    headline: "Jamaica Schools Guide",
    headlineAccent: "Your Complete Education Resource",
    description: "PEP, CSEC & CAPE exam guides, school rankings, scholarships, UWI & UTech admissions, HEART Trust training & more — everything Jamaican students and parents need in one place.",
    primaryCta: "Find Schools",
    secondaryCta: "Exam Guides"
  }
};

// ==================== HERO CAROUSEL COMPONENT ====================
export const HeroCarousel = ({ setPage, lang = 'en', aside = null }) => {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasLoadedSlides, setHasLoadedSlides] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  // AdminHeroCarousel uploads an optional mobile crop to
  // `background_image_url_mobile` and tells the admin to supply one, but the
  // render path only ever read the desktop URL — so phones got the wide asset
  // with the wrong composition, and the second upload slot did nothing.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  const intervalRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      const [slidesRes, settingsRes] = await Promise.all([
        heroCarousel.getActiveSlides(),
        heroCarousel.getSettings()
      ]);
      setSlides(slidesRes.data || []);
      setSettings(settingsRes.data || {
        autoplay: true,
        autoplay_interval: 5000,
        transition_style: 'fade',
        transition_duration: 500,
        pause_on_hover: true,
        show_indicators: true,
        show_arrows: true,
        loop: true
      });
      setHasLoadedSlides(true);
      // Trigger reveal animation after a brief delay for smooth entrance
      setTimeout(() => setIsRevealed(true), 50);
    };
    loadData();
  }, []);

  // Navigate to next slide
  const nextSlide = useCallback(() => {
    if (slides.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (prev >= slides.length - 1) {
          return settings?.loop ? 0 : prev;
        }
        return prev + 1;
      });
      setIsTransitioning(false);
    }, settings?.transition_duration || 500);
  }, [slides.length, settings]);

  // Navigate to previous slide
  const prevSlide = useCallback(() => {
    if (slides.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => {
        if (prev <= 0) {
          return settings?.loop ? slides.length - 1 : prev;
        }
        return prev - 1;
      });
      setIsTransitioning(false);
    }, settings?.transition_duration || 500);
  }, [slides.length, settings]);

  // Go to specific slide
  const goToSlide = useCallback((index) => {
    if (index === currentIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, settings?.transition_duration || 500);
  }, [currentIndex, settings]);

  // Autoplay
  useEffect(() => {
    if (!settings?.autoplay || slides.length <= 1) return;
    if (settings?.pause_on_hover && isHovered) return;

    intervalRef.current = setInterval(nextSlide, settings?.autoplay_interval || 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [settings, slides.length, isHovered, nextSlide]);

  // Handle button click
  const handleButtonClick = (button) => {
    if (button.linkType === 'external') {
      window.open(button.link, '_blank');
    } else {
      setPage(button.link);
    }
  };

  // Get transition classes based on style
  const getTransitionClasses = () => {
    const duration = settings?.transition_duration || 500;
    const base = `transition-all duration-[${duration}ms]`;

    switch (settings?.transition_style) {
      case 'slide':
        return isTransitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0';
      case 'zoom':
        return isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100';
      case 'flip':
        return isTransitioning ? 'opacity-0 rotate-y-90' : 'opacity-100 rotate-y-0';
      case 'fade':
      default:
        return isTransitioning ? 'opacity-0' : 'opacity-100';
    }
  };

  // While loading, show placeholder with subtle pulse animation
  if (!hasLoadedSlides) {
    return (
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900 min-h-[200px] md:min-h-[280px] animate-pulse" />
    );
  }

  // After loading, if no slides exist, show default content
  if (slides.length === 0) {
    const t = T[lang] || T.en;
    return (
      <section className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-900 via-brand-800 to-brand-900 text-white pt-8 pb-12 md:pt-12 md:pb-16 px-6 sm:px-10 md:px-20 transition-all duration-700 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand-300 rounded-full blur-3xl" />
        </div>
        <div className={`relative z-10 transition-all duration-700 delay-200 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            {t.headline}<br />
            <span className="text-yellow-400">{t.headlineAccent}</span>
          </h2>
          <p className="text-lg text-brand-100 max-w-2xl mb-8">
            {t.description}
          </p>
          <div className={`flex flex-wrap gap-4 transition-all duration-700 delay-400 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              // "Find Schools" means all 1,654 institutions, not the 231 on the
              // High Schools door — /all-schools is the only page that delivers
              // what the label promises.
              onClick={() => setPage('all-schools')}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-brand-900 font-bold px-6 py-3 rounded-xl transition-colors"
            >
              <Shield className="w-5 h-5" /> {t.primaryCta}
            </button>
            <button
              onClick={() => setPage('exams')}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-xl border border-white/30 transition-colors"
            >
              <Search className="w-5 h-5" /> {t.secondaryCta}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const currentSlide = slides[currentIndex];
  // The prev/next arrows sit at left-4/right-4 and are ~56px wide with their
  // padding, which is the only reason the content carried px-16. They only
  // render for multiple slides, so a single slide was paying 128px of side
  // padding on a 320px phone — 40% of the screen — which wrapped the heading
  // to five lines. Reserve the clearance only when the arrows are actually
  // there.
  const hasArrows = settings?.show_arrows && slides.length > 1;
  const padX = hasArrows ? 'px-16 md:px-20' : 'px-6 sm:px-10 md:px-20';
  // Mobile crop where one exists, desktop asset otherwise.
  const backgroundUrl =
    (isNarrow && currentSlide.background_image_url_mobile) || currentSlide.background_image_url;

  return (
    <section
      className={`relative overflow-hidden rounded-3xl transition-all duration-700 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${getTransitionClasses()}`}
        style={currentSlide.background_type === 'image' && backgroundUrl ? {
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        {currentSlide.background_type !== 'image' && (
          <div className={`absolute inset-0 bg-gradient-to-br ${currentSlide.background_gradient || 'from-brand-900 via-brand-800 to-brand-900'}`} />
        )}

        {/* Overlay for images */}
        {currentSlide.background_type === 'image' && (
          <div
            className="absolute inset-0 bg-black"
            style={{ opacity: currentSlide.background_overlay_opacity || 0.3 }}
          />
        )}

        {/* Decorative element */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand-300 rounded-full blur-3xl" />
        </div>
      </div>

      {/* Content */}
      <div className={`relative z-10 pt-8 pb-12 md:pt-12 md:pb-16 ${padX} text-white transition-all duration-500 ${getTransitionClasses()}`}>
        {/* Heading and subtext stay capped at max-w-2xl — prose needs a
            measure. The CTA row below deliberately does NOT: buttons plus the
            four figures need ~870px, so inside a 672px column they could never
            share a line and the stats always wrapped underneath, which is not
            what was asked for. Measure control matters for reading, not for a
            row of controls. */}
        <div className={`${
          currentSlide.text_alignment === 'center' ? 'text-center mx-auto' :
          currentSlide.text_alignment === 'right' ? 'text-right ml-auto' : ''
        } max-w-2xl`}>
          <h2 className={`text-4xl md:text-5xl font-black mb-4 transition-all duration-700 delay-150 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {currentSlide.heading}
            {currentSlide.heading_highlight && (
              <>
                <br />
                <span className={`text-${currentSlide.accent_color || 'yellow-400'}`}>
                  {currentSlide.heading_highlight}
                </span>
              </>
            )}
          </h2>

          {currentSlide.subtext && (
            <p className={`text-lg text-white/80 mb-8 transition-all duration-700 delay-300 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {currentSlide.subtext}
            </p>
          )}

        </div>

        <div className={
          currentSlide.text_alignment === 'center' ? 'text-center' :
          currentSlide.text_alignment === 'right' ? 'text-right' : ''
        }>
          {/* CTA row. `aside` shares this row with the slide's own buttons —
              the homepage puts the headline figures here, so the banner carries
              the promotion and the proof on one line. The row is rendered when
              EITHER exists, so a slide with no buttons still shows the figures.
              Stacks below lg, where a button and four figures cannot share a
              line without wrapping badly. */}
          {((currentSlide.buttons && currentSlide.buttons.length > 0) || aside) && (
          <div className={`flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-5 lg:gap-8 transition-all duration-700 delay-500 ease-out ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {currentSlide.buttons && currentSlide.buttons.length > 0 && (
            <div className={`flex flex-wrap gap-4 shrink-0 ${
              currentSlide.text_alignment === 'center' ? 'justify-center' :
              currentSlide.text_alignment === 'right' ? 'justify-end' : ''
            }`}>
              {currentSlide.buttons.map((button, index) => {
                const IconComponent = button.icon ? ICONS[button.icon] : null;
                const isPrimary = button.style === 'primary';

                return (
                  <button
                    key={index}
                    onClick={() => handleButtonClick(button)}
                    className={`flex items-center gap-2 font-bold px-6 py-3 rounded-xl whitespace-nowrap transition-colors ${
                      isPrimary
                        ? 'bg-yellow-500 hover:bg-yellow-400 text-brand-900'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/30'
                    }`}
                  >
                    {IconComponent && <IconComponent className="w-5 h-5" />}
                    {button.text}
                  </button>
                );
              })}
            </div>
          )}
          {aside && <div className="shrink-0 lg:ml-auto">{aside}</div>}
          </div>
          )}
        </div>
      </div>

      {/* Navigation Arrows */}
      {settings?.show_arrows && slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors z-20"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors z-20"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Indicators */}
      {settings?.show_indicators && slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;
